import { makeAutoObservable, runInAction } from "mobx";
import type { ApiClient } from "@/clients/ApiClient";
import type { ImportApplied, ImportQueued, ImportReport } from "@/generated/api";

type ImportApi = Pick<ApiClient, "post">;

export type ImportSource = "opml" | "links";

/**
 * A listener's import: read a list, look at what it matched, then follow what they choose. The report is only
 * kept here until they apply it or leave the page; the API keeps it for a day.
 */
export class ImportStore {
    report: ImportReport | null = null;
    busy = false;

    constructor(private readonly client: ImportApi) {
        makeAutoObservable<ImportStore, "client">(this, { client: false });
    }

    /** Reads the list and reports what matches. Nothing is followed. */
    async read(source: ImportSource, content: string): Promise<ImportReport> {
        runInAction(() => (this.busy = true));
        try {
            const report = await this.client.post<ImportReport>("/me/imports", { source, content });
            runInAction(() => (this.report = report));
            return report;
        } finally {
            runInAction(() => (this.busy = false));
        }
    }

    /** Follows the chosen podcasts from the report. Returns how many were newly followed. */
    async apply(podcastIds: number[]): Promise<number> {
        if (!this.report) throw new Error("No import to apply");
        const result = await this.client.post<ImportApplied>(`/me/imports/${this.report.id}/apply`, {
            podcast_ids: podcastIds,
        });
        runInAction(() => (this.report = null));
        return result.followed;
    }

    /** Asks for the feeds in the report that did not match to be added. Returns what was queued. */
    async queueUnmatched(): Promise<ImportQueued> {
        if (!this.report) throw new Error("No import to queue");
        return this.client.post<ImportQueued>(`/me/imports/${this.report.id}/queue`, {});
    }

    clear() {
        this.report = null;
    }
}
